import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ArrowLeft, X, Clock, ChefHat, Lightbulb, ArrowRight, Link2, Check, Sparkles, Plus } from 'lucide-react';
import { recipes, recipeCategories, recipeTags, systemEnhancing } from '../data/recipes';
import NewsletterSignup from '../components/NewsletterSignup';
import useNewsletterPrompt from '../hooks/useNewsletterPrompt';
import { useResourceDeepLink } from '../hooks/useResourceDeepLink';
import useShoppingList from '../hooks/useShoppingList';
import ShoppingListDrawer from '../components/ShoppingListDrawer';
import { useAuth } from '../context/AuthContext';
import useFoodPreferences from '../hooks/useFoodPreferences';
import { matchRecipe } from '../utils/recipeMatch';
import { RecipeMatchBanner, RecipeMatchToggle, RecipeMatchBadges, RecipeMatchNote } from '../components/RecipeMatch';

const ResourcesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeTag, setActiveTag] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [matchOnly, setMatchOnly] = useState(false);
  const { toggleItem, isInList } = useShoppingList();
  const { user, hasAccess } = useAuth();
  const isVip = !!user && hasAccess('vip');
  const foodPrefs = useFoodPreferences(isVip ? user : null);
  const matches = useMemo(() => {
    if (!foodPrefs?.found) return null;
    return Object.fromEntries(recipes.map((r) => [r.id, matchRecipe(r, foodPrefs.love, foodPrefs.avoid)]));
  }, [foodPrefs]);
  const matchCount = matches ? Object.values(matches).filter((m) => m.loved.length).length : 0;
  
  // Newsletter soft prompt
  const { showPrompt, dismissPrompt } = useNewsletterPrompt('recipes-resources');

  // Deep linking for recipes
  const { setResourceParam, clearResourceParam, copyResourceLink, copied } = useResourceDeepLink({
    resources: recipes,
    getDetailedContent: null,
    onSelect: (recipe) => {
      setSelectedRecipe(recipe);
      if (recipe?.category) setSelectedCategory(recipe.category);
    },
  });

  const handleRecipeClick = (recipe) => {
    setSelectedRecipe(recipe);
    setResourceParam(recipe.id);
  };

  const handleCloseRecipe = () => {
    setSelectedRecipe(null);
    clearResourceParam();
  };

  // Get recipe counts per category
  const getCategoryCount = (categoryId) => {
    if (categoryId === 'all') return recipes.length;
    const cat = recipeCategories.find(c => c.id === categoryId);
    const ids = cat?.match || [categoryId];
    return recipes.filter(r => ids.includes(r.category) || (r.categories && r.categories.some(c => ids.includes(c)))).length;
  };

  // Count recipes carrying a given tag (system tags also count Rainbow recipes)
  const RAINBOW = 'Rainbow / All Systems';
  const hasTag = (r, id) => r.tag === id || (Array.isArray(r.tags) && r.tags.includes(id));
  const getTagCount = (tagId) => recipes.filter(r => hasTag(r, tagId) || (systemEnhancing[tagId] && hasTag(r, RAINBOW))).length;

  // Split tags into body-systems vs dietary types
  const systemTags = recipeTags.filter(t => systemEnhancing[t.id]);
  const dietaryTags = recipeTags.filter(t => t.id !== 'all' && t.id !== RAINBOW && !systemEnhancing[t.id]);

  // Filter recipes based on selected category and tag
  const selectedCategoryObj = recipeCategories.find(c => c.id === selectedCategory);
  const selectedCategoryIds = selectedCategoryObj?.match || (selectedCategory ? [selectedCategory] : []);
  const filteredRecipes = recipes.filter(recipe => {
    const matchesCategory = !selectedCategory || selectedCategory === 'all' || selectedCategoryIds.includes(recipe.category) || (recipe.categories && recipe.categories.some(c => selectedCategoryIds.includes(c)));
    const matchesTag = activeTag === 'all'
      || recipe.tag === activeTag
      || (Array.isArray(recipe.tags) && recipe.tags.includes(activeTag))
      || (systemEnhancing[activeTag] && Array.isArray(recipe.tags) && recipe.tags.includes('Rainbow / All Systems'));
    const matchesSearch = recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         recipe.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesTag && matchesSearch && (!matchOnly || !!matches?.[recipe.id]?.loved.length);
  });
  if (matchOnly && matches) {
    filteredRecipes.sort((a, b) => (matches[b.id].loved.length - matches[b.id].avoided.length) - (matches[a.id].loved.length - matches[a.id].avoided.length));
  }

  const getTagColor = (tag) => {
    if (tag === 'Detox Friendly') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (tag === 'CRAP Cleanse Friendly') return 'bg-amber-100 text-amber-700 border-amber-200';
    if (tag === 'Gluten Free') return 'bg-stone-100 text-stone-700 border-stone-300';
    if (tag === 'Red / Root') return 'bg-red-100 text-red-700 border-red-200';
    if (tag === 'Orange / Flow') return 'bg-orange-100 text-orange-700 border-orange-200';
    if (tag === 'Yellow / Fire') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    if (tag === 'Green / Love') return 'bg-green-100 text-green-700 border-green-200';
    if (tag === 'Blue / Truth') return 'bg-sky-100 text-sky-700 border-sky-200';
    if (tag === 'Indigo / Insight') return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    if (tag === 'Violet / Spirit') return 'bg-violet-100 text-violet-700 border-violet-200';
    if (tag === 'Rainbow / All Systems') return 'bg-gradient-to-r from-red-200 via-yellow-200 to-violet-200 text-stone-700 border-stone-200';
    return 'bg-stone-100 text-stone-700';
  };

  const getSizeClass = (index) => {
    const pattern = index % 6;
    if (pattern === 0 || pattern === 3) return 'aspect-[3/4]';
    if (pattern === 2 || pattern === 5) return 'aspect-square';
    return 'aspect-[4/3]';
  };

  // Category Landing View
  if (!selectedCategory) {
    return (
      <div className="bg-cream pt-24 min-h-screen" data-testid="resources-page">
        {/* Hero Section */}
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
                Nourish Your Body
              </p>
              <h1 className="font-heading text-5xl md:text-6xl text-forest leading-[1.1] mb-6">
                Recipes & Resources
              </h1>
              <p className="text-stone-600 text-lg font-body leading-relaxed mb-4">
                Simple, grounded recipes aligned with gluten-free and anti-inflammatory principles. Delicious and genuinely good for us.
              </p>
              <p className="text-stone-600 font-body leading-relaxed mb-4">
                I'm often looking for ways to support optimal wellness wherever I can, quietly sneaking in "good for me" meals to help balance real life. The majority of these recipes are Detox-friendly and SCD-friendly, with most being naturally gluten-free and grain-free.
              </p>
              <p className="text-stone-600 font-body leading-relaxed mb-4">
                There is a dedicated gluten-free section that includes gluten-free flours; the rest of the recipes rely on whole foods and are naturally free of flour and gluten products.
              </p>
              <p className="text-stone-600 font-body leading-relaxed mb-6">
                This is back-to-basics nourishment designed to support your healing journey in a way that feels easy and accessible.
              </p>
              
              {/* Disclaimer Section */}
              <div className="bg-stone-100 rounded-2xl p-6 text-left">
                <p className="text-stone-500 font-body text-sm leading-relaxed mb-3">
                  <span className="font-semibold text-stone-600">Please note:</span> This content is not medical advice. For medical conditions, please consult your doctor. What I offer is wellness guidance and inspiration for supporting the body as it moves toward greater balance. Through whole-foods eating and reducing inflammation in our daily lives, meaningful change is possible.
                </p>
                <p className="text-stone-500 font-body text-sm leading-relaxed">
                  My philosophy is rooted in terrain theory and in keeping the body as healthy and resilient as we can. This is supportive guidance, not a replacement for medical care, but it is designed to explore areas where conventional systems often fall short. <span className="italic">You can't get healthy in THE sick system.</span>
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        <RecipeMatchBanner
          prefs={foodPrefs}
          isVip={isVip}
          count={matchCount}
          onOpen={() => { setMatchOnly(true); setActiveTag('all'); setSelectedCategory('all'); window.scrollTo(0, 0); }}
        />

        {/* Shop By Your System + Type Filters */}
        <section className="py-4" data-testid="recipe-tag-legend">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {/* Shop by your system */}
            <div className="bg-white rounded-3xl shadow-card border border-stone-100 p-6 md:p-8 mb-8">
              <div className="text-center mb-6">
                <p className="text-terracotta font-body text-xs tracking-widest uppercase mb-2">Eat for your body</p>
                <h2 className="font-heading text-2xl md:text-3xl text-forest">Shop by Your System</h2>
                <p className="text-stone-500 font-body text-sm mt-2 max-w-xl mx-auto">
                  Know your primary system from a quiz or session? Tap it to see every recipe that supports it.
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {systemTags.map((tag) => {
                  const count = getTagCount(tag.id);
                  const sys = systemEnhancing[tag.id];
                  return (
                    <button
                      key={tag.id}
                      onClick={() => { setActiveTag(tag.id); setSelectedCategory('all'); window.scrollTo(0, 0); }}
                      data-testid={`system-filter-${tag.id}`}
                      disabled={count === 0}
                      className={`group flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-all duration-300 ${tag.color} border-transparent hover:shadow-md hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none`}
                    >
                      <span className="w-3 h-3 rounded-full bg-current opacity-70 flex-shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block font-heading text-base leading-tight">{sys.name}</span>
                        <span className="block font-body text-xs opacity-70 truncate">{tag.name}</span>
                      </span>
                      <span className="font-body text-xs font-semibold opacity-70 flex-shrink-0">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Filter by type */}
            <p className="text-center text-stone-500 font-body text-sm mb-4">
              Or filter by type:
            </p>
            <div className="flex flex-wrap gap-3 justify-center items-center mb-8">
              {dietaryTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => { setActiveTag(tag.id); setSelectedCategory('all'); window.scrollTo(0, 0); }}
                  data-testid={`legend-filter-${tag.id}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-200 font-body text-sm transition-all duration-300 hover:shadow-sm hover:-translate-y-0.5 ${tag.color}`}
                >
                  <span className="w-2 h-2 rounded-full bg-current opacity-60"></span>
                  {tag.name}
                  <span className="opacity-60">({getTagCount(tag.id)})</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Category Cards Grid */}
        <section className="py-8 pb-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recipeCategories.filter(cat => cat.id !== 'all').map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  onClick={() => setSelectedCategory(category.id)}
                  data-testid={`category-card-${category.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-2xl bg-white shadow-card hover:shadow-lg transition-all duration-500 h-full">
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-forest/20 to-transparent" />
                      
                      {/* Recipe Count Badge */}
                      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                        <span className="font-body text-sm font-medium text-forest">
                          {getCategoryCount(category.id)} recipes
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="font-heading text-2xl text-forest mb-2 group-hover:text-terracotta transition-colors">
                        {category.name}
                      </h3>
                      <p className="text-stone-500 font-body text-sm mb-4">
                        {category.description}
                      </p>
                      <div className="flex items-center gap-2 text-terracotta font-body text-sm font-medium group-hover:gap-3 transition-all">
                        View Recipes
                        <ArrowRight size={16} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Coming Soon Note */}
        <section className="py-16 bg-stone-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-heading text-3xl md:text-4xl text-forest mb-4">
                More Recipes Coming Soon
              </h2>
              <p className="text-stone-600 text-lg font-body leading-relaxed">
                I'm continuously adding new recipes and resources. Sign up for my newsletter 
                to be notified when new content is available.
              </p>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  // Recipe List View (when category is selected)
  const currentCategory = recipeCategories.find(c => c.id === selectedCategory);

  return (
    <div className="bg-cream pt-24 min-h-screen" data-testid="resources-page">
      {/* Back Button & Header */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <button
              onClick={() => { setSelectedCategory(null); setMatchOnly(false); }}
              data-testid="back-to-categories"
              className="flex items-center gap-2 text-stone-500 hover:text-forest font-body mb-6 transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Categories
            </button>

            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
              <div>
                <h1 className="font-heading text-4xl md:text-5xl text-forest">
                  {currentCategory?.name}
                </h1>
                <p className="text-stone-500 font-body mt-2">{currentCategory?.description}</p>
              </div>
              
              {/* Search */}
              <div className="relative w-full md:w-72">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-stone-400" size={18} />
                <input
                  type="text"
                  placeholder="Search recipes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="resources-search"
                  className="w-full bg-white border border-stone-300 rounded-full pl-11 pr-4 py-2.5 text-forest text-sm placeholder:text-stone-400 focus:outline-none focus:border-terracotta"
                />
              </div>
            </div>

            {/* Tag Filters */}
            <div className="flex flex-wrap gap-3 mb-8">
              {recipeTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => setActiveTag(tag.id)}
                  data-testid={`tag-filter-${tag.id}`}
                  className={`px-4 py-2 rounded-full font-body text-sm border transition-all duration-300 ${
                    activeTag === tag.id
                      ? tag.id === 'Detox Friendly' 
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : tag.id === 'CRAP Cleanse Friendly'
                        ? 'bg-amber-600 text-white border-amber-600'
                        : 'bg-forest text-cream border-forest'
                      : 'bg-white text-stone-600 border-stone-300 hover:border-stone-400'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
              {matches && <RecipeMatchToggle on={matchOnly} onToggle={() => setMatchOnly((v) => !v)} />}
              <span className="text-stone-400 font-body text-sm self-center ml-2">
                {filteredRecipes.length} recipes
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Recipe Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
            {filteredRecipes.map((recipe, index) => (
              <motion.div
                key={recipe.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.03 }}
                data-testid={`recipe-card-${recipe.id}`}
                className="break-inside-avoid group cursor-pointer"
                onClick={() => handleRecipeClick(recipe)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-card hover:shadow-lg transition-all duration-500">
                  <div className={`relative ${getSizeClass(index)}`}>
                    <img
                      src={recipe.image}
                      alt={recipe.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {/* Tag Badge(s) */}
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 max-w-[calc(100%-1.5rem)]">
                      {(recipe.tags && recipe.tags.length > 0 ? recipe.tags : (recipe.tag ? [recipe.tag] : [])).map((t) => (
                        <span
                          key={t}
                          className={`inline-block px-3 py-1 rounded-full font-body text-xs border ${getTagColor(t)}`}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-forest/80 via-forest/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    {/* View Recipe on hover */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <span className="bg-white text-forest px-4 py-2 rounded-full font-body text-sm font-medium">
                        View Recipe
                      </span>
                    </div>
                  </div>
                  
                  {/* Title below image */}
                  <div className="p-4">
                    <h3 className="font-heading text-lg text-forest group-hover:text-terracotta transition-colors">
                      {recipe.title}
                    </h3>
                    <RecipeMatchBadges match={matches?.[recipe.id]} testId={`recipe-match-badges-${recipe.id}`} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Empty State */}
          {filteredRecipes.length === 0 && (
            <div className="text-center py-16">
              <p className="text-stone-500 font-body text-lg">
                No recipes found matching your filters.
              </p>
              <button
                onClick={() => { setActiveTag('all'); setSearchTerm(''); setMatchOnly(false); }}
                className="mt-4 text-terracotta font-body hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Recipe Modal */}
      <AnimatePresence>
        {selectedRecipe && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-forest/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleCloseRecipe}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-cream rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="recipe-modal"
            >
              {/* Modal Header */}
              <div className="relative">
                <img
                  src={selectedRecipe.image}
                  alt={selectedRecipe.title}
                  className="w-full h-48 object-cover rounded-t-2xl"
                />
                <div className="absolute top-4 right-4 flex gap-2">
                  <button
                    onClick={() => copyResourceLink(selectedRecipe.id)}
                    className="bg-white/90 rounded-full p-2 hover:bg-white transition-colors"
                    title="Copy shareable link"
                    data-testid="copy-resource-link"
                  >
                    {copied ? <Check size={20} className="text-emerald-600" /> : <Link2 size={20} className="text-forest" />}
                  </button>
                  <button
                    onClick={handleCloseRecipe}
                    className="bg-white/90 rounded-full p-2 hover:bg-white transition-colors"
                    data-testid="close-recipe-modal"
                  >
                    <X size={20} className="text-forest" />
                  </button>
                </div>
                {copied && (
                  <div className="absolute top-16 right-4 bg-emerald-600 text-white text-xs font-body px-3 py-1 rounded-full shadow-lg">
                    Link copied!
                  </div>
                )}
                <div className="absolute bottom-4 left-4 flex flex-wrap gap-1.5 max-w-[calc(100%-2rem)]">
                  {(selectedRecipe.tags && selectedRecipe.tags.length > 0 ? selectedRecipe.tags : (selectedRecipe.tag ? [selectedRecipe.tag] : [])).map((t) => (
                    <span
                      key={t}
                      className={`inline-block px-3 py-1 rounded-full font-body text-xs border ${getTagColor(t)}`}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 md:p-8">
                <h2 className="font-heading text-3xl text-forest mb-3">
                  {selectedRecipe.title}
                </h2>
                <p className="text-stone-600 font-body mb-6">
                  {selectedRecipe.description}
                </p>

                {/* Enhancing — what this color/system supports in the body */}
                {(() => {
                  const tagsToCheck = selectedRecipe.tags && selectedRecipe.tags.length > 0
                    ? selectedRecipe.tags
                    : (selectedRecipe.tag ? [selectedRecipe.tag] : []);
                  const isRainbow = tagsToCheck.includes('Rainbow / All Systems');
                  if (isRainbow) {
                    return (
                      <div className="mb-6 rounded-xl p-4 border border-stone-200 bg-white" data-testid="enhancing-section">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles size={18} className="text-terracotta" />
                          <h4 className="font-body font-semibold text-forest">
                            Enhancing — All Seven Systems
                          </h4>
                        </div>
                        <p className="text-xs font-body text-stone-500 mb-3">
                          A rainbow recipe — nourishing across every system, from The Root to The Spirit.
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.values(systemEnhancing).map((s) => (
                            <span
                              key={s.name}
                              className={`text-xs font-body px-2.5 py-1 rounded-full border ${s.pill}`}
                            >
                              {s.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  const systemTag = tagsToCheck.find((t) => systemEnhancing[t]);
                  if (!systemTag) return null;
                  const sys = systemEnhancing[systemTag];
                  return (
                    <div className="mb-6 rounded-xl p-4 border border-stone-200 bg-white" data-testid="enhancing-section">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={18} className="text-terracotta" />
                        <h4 className="font-body font-semibold text-forest">
                          Enhancing — {sys.name}
                        </h4>
                      </div>
                      <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">
                        Endocrine gland · {sys.gland}
                      </p>
                      <p className="text-xs font-body text-stone-500 uppercase tracking-wider mb-2">
                        Anatomy supported
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {sys.anatomy.map((a) => (
                          <span
                            key={a}
                            className={`text-xs font-body px-2.5 py-1 rounded-full border ${sys.pill}`}
                          >
                            {a}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Ingredients */}
                <RecipeMatchNote match={matches?.[selectedRecipe.id]} />
                <div className="mb-6">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <ChefHat size={20} className="text-terracotta" />
                      <h3 className="font-heading text-xl text-forest">Ingredients</h3>
                    </div>
                    {(() => {
                      const idxs = selectedRecipe.ingredients
                        .map((ing, i) => ({ ing, i }))
                        .filter(({ ing }) => !ing.trim().startsWith('---'));
                      const allAdded = idxs.length > 0 && idxs.every(({ i }) => isInList(selectedRecipe.id, i));
                      return (
                        <button
                          type="button"
                          data-testid="add-all-ingredients-btn"
                          onClick={() =>
                            idxs.forEach(({ ing, i }) => {
                              const inList = isInList(selectedRecipe.id, i);
                              if (allAdded ? inList : !inList) {
                                toggleItem(selectedRecipe.id, selectedRecipe.title, i, ing);
                              }
                            })
                          }
                          className="inline-flex items-center gap-1.5 rounded-full bg-forest text-cream text-xs font-body font-medium px-3 py-1.5 hover:bg-forest-dark transition-colors flex-shrink-0"
                        >
                          {allAdded ? <Check size={13} /> : <Plus size={13} />}
                          {allAdded ? 'Added — remove all' : 'Add all to list'}
                        </button>
                      );
                    })()}
                  </div>
                  <p className="text-xs font-body text-stone-400 mb-4">Tick items to add them to your shopping list.</p>
                  <ul className="space-y-2">
                    {selectedRecipe.ingredients.map((ingredient, i) => {
                      const isHeader = ingredient.trim().startsWith('---');
                      if (isHeader) {
                        return (
                          <li key={i} className="pt-3 first:pt-0">
                            <span className="text-xs font-body font-semibold uppercase tracking-wide text-forest/60">
                              {ingredient.replace(/^[-\s]+|[-\s]+$/g, '')}
                            </span>
                          </li>
                        );
                      }
                      const checked = isInList(selectedRecipe.id, i);
                      return (
                        <li key={i}>
                          <label
                            className="flex items-start gap-3 text-stone-600 font-body cursor-pointer group"
                            data-testid={`ingredient-check-${i}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleItem(selectedRecipe.id, selectedRecipe.title, i, ingredient)}
                              className="mt-1 w-4 h-4 rounded border-stone-300 accent-terracotta cursor-pointer flex-shrink-0"
                            />
                            <span className={checked ? 'text-terracotta' : 'group-hover:text-forest transition-colors'}>
                              {ingredient}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Instructions */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock size={20} className="text-terracotta" />
                    <h3 className="font-heading text-xl text-forest">Instructions</h3>
                  </div>
                  <ol className="space-y-3">
                    {selectedRecipe.instructions.map((step, i) => (
                      <li key={i} className="flex gap-4 text-stone-600 font-body">
                        <span className="w-6 h-6 bg-forest text-cream rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">
                          {i + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Tips */}
                {selectedRecipe.tips && (
                  <div className="bg-terracotta/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb size={18} className="text-terracotta" />
                      <h4 className="font-body font-semibold text-forest">Jill's Tips</h4>
                    </div>
                    <p className="text-stone-600 font-body text-sm whitespace-pre-line">
                      {selectedRecipe.tips}
                    </p>
                  </div>
                )}

                {/* Pairs With / Related Recipes */}
                {selectedRecipe.relatedIds && selectedRecipe.relatedIds.length > 0 && (
                  <div className="mt-6" data-testid="related-recipes-section">
                    <div className="flex items-center gap-2 mb-3">
                      <Link2 size={18} className="text-terracotta" />
                      <h4 className="font-body font-semibold text-forest">Pairs With</h4>
                    </div>
                    <div className="space-y-2">
                      {selectedRecipe.relatedIds.map((relId) => {
                        const rel = recipes.find((r) => r.id === relId);
                        if (!rel) return null;
                        return (
                          <button
                            key={rel.id}
                            onClick={() => handleRecipeClick(rel)}
                            data-testid={`related-recipe-${rel.id}`}
                            className="w-full flex items-center gap-3 bg-white hover:bg-cream border border-stone-200 rounded-xl p-3 transition-colors text-left group"
                          >
                            <img
                              src={rel.image}
                              alt={rel.title}
                              className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-body font-semibold text-forest group-hover:text-terracotta transition-colors">
                                {rel.title}
                              </p>
                              <p className="font-body text-xs text-stone-500 line-clamp-1">
                                {rel.description}
                              </p>
                            </div>
                            <span className="font-body text-xs text-terracotta whitespace-nowrap">
                              View →
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Newsletter Soft Prompt */}
      <AnimatePresence>
        {showPrompt && (
          <NewsletterSignup 
            variant="prompt" 
            onClose={dismissPrompt} 
            source="recipes-resources" 
          />
        )}
      </AnimatePresence>

      <ShoppingListDrawer />
    </div>
  );
};

export default ResourcesPage;
